import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { Colors, printStyle, Shapes } from 'src/components/theme'
import { Close } from '@emotion-icons/material/Close'
import { Insight } from 'src/components/insights/types'
import { Typography } from '@mui/material'
import { DotDivider } from 'src/components/generic/dot-divider'
import { useParsedRouter } from 'src/layout/hooks/use-parsed-router'
import { CircleIcon } from 'src/components/icons/circle-icon'
import { rgbaStrOpacity } from 'src/utils/colors'
import { useAtomValueLoadable } from 'src/utils/jotai'
import { fullAddress } from 'src/utils/full-address'

import * as coreAtoms from 'src/v2/state/core'

const InsightZipped = dynamic(() => import('src/components/insight-zipped/index'), {
  ssr: false,
})

export const TopToolbarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Shapes.spacing[6]}px;
`

export const TopToolbarLeft = styled.div`
  display: flex;
`

type IconProps = {
  color: string
}

export const TopToolbarIconBox = styled.div<IconProps>`
  width: ${Shapes.insightsPageTopIconBoxSize}px;
  height: ${Shapes.insightsPageTopIconBoxSize}px;
  border-radius: ${Shapes.borderRadius}px;
  border: 1px solid ${(props) => props.color};
  background: ${Colors.bgWhite};
  padding: ${Shapes.spacing[1]}px;
  margin-right: ${Shapes.spacing[4]}px;
`

export const TopToolbarIconBoxContent = styled.div<IconProps>`
  background: ${(props) => rgbaStrOpacity(props.color, 0.1)};
  width: 100%;
  height: 100%;
  border-radius: ${Shapes.borderRadius}px;
  display: flex;
  align-items: center;
  justify-content: center;

  & svg {
    fill: ${(props) => props.color};
  }
`

export const TopToolbarTitle = styled.div`
  height: ${Shapes.insightsPageTopIconBoxSize}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  p {
    display: flex;
    align-items: center;
  }
`

const TopToolbarTitleWrapper = styled.div`
  display: flex;
`

export const TopToolbarSubTitle = styled.div`
  margin-top: ${Shapes.spacing[1]}px;
`

const TopToolbarRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${Shapes.spacing[2]}px;
`

const CloseButton = styled(Close)`
  width: 20px;
  height: 20px;
  fill: ${Colors.textBlack};
  cursor: pointer;

  &:hover {
    fill: ${Colors.textDark};
  }
  ${printStyle`
  display: none;
  `}
`

type Props = {
  insight: Insight
  subtitle?: string
  color?: string
  actions?: React.ReactNode
}

export const TopToolbar: React.FC<Props> = (props) => {
  const { insight, subtitle, color = Colors.blueDark, actions } = props
  const { query, router } = useParsedRouter()
  const selectedEntityLoadable = useAtomValueLoadable(coreAtoms.selectedEntity)

  const closeRedir = useMemo(() => {
    if (router.asPath.includes('owner-reveal')) {
      return {
        pathname: '/owner-reveal',
        query: query,
      }
    }
    return {
      pathname: '/',
      query: query,
    }
  }, [router.asPath, query])

  const insightInfo = useMemo(() => {
    if (selectedEntityLoadable.state !== 'hasData' || !selectedEntityLoadable.data) {
      return null
    }
    const selectedEntity = selectedEntityLoadable.data
    switch (selectedEntity.type) {
      case 'SELECTED_LOCATION':
      case 'SELECTED_SHOPPING_CENTER':
        return {
          name: selectedEntity.data['am-loc'].name,
          address: fullAddress(selectedEntity.data['am-loc']),
        }
      case 'SELECTED_ADDRESS':
        return {
          name: selectedEntity.data.name,
          address: selectedEntity.data.formatted_address,
        }
      default:
        return null
    }
  }, [selectedEntityLoadable])

  if (selectedEntityLoadable.state !== 'hasData') {
    return null
  }

  const icon = React.createElement(
    insight.icon as React.FC<{ size?: number; color?: string; style?: React.CSSProperties }>,
    {
      size: Shapes.insightsPageTopIconSize,
      color: color,
      style: { verticalAlign: 'sub' },
    }
  )
  const nameIcon = insight.nameIcon
    ? React.createElement(
        insight.nameIcon as React.FC<{ size?: number; color?: string; style?: React.CSSProperties }>,
        {
          size: Shapes.insightsPageTopIconSize,
          color: 'white',
          style: { verticalAlign: 'sub' },
        }
      )
    : null
  const nameIconColor = insight.nameIconColor

  const subtileComponent =
    subtitle ||
    (insightInfo && (
      <>
        {insightInfo.name}
        <DotDivider />
        {insightInfo.address}
      </>
    ))

  return (
    <TopToolbarWrapper>
      <TopToolbarLeft>
        <TopToolbarIconBox color={color}>
          <TopToolbarIconBoxContent color={color}>{icon}</TopToolbarIconBoxContent>
        </TopToolbarIconBox>
        <TopToolbarTitle>
          <TopToolbarTitleWrapper>
            <Typography variant="header3" color={Colors.textBlack}>
              {insight.name}
            </Typography>
            {nameIcon && (
              <CircleIcon
                style={{
                  marginLeft: Shapes.spacing[2],
                }}
                size={Shapes.insightsPageTopIconSize}
                padding={5}
                color={nameIconColor || Colors.blueDark}>
                {nameIcon}
              </CircleIcon>
            )}
          </TopToolbarTitleWrapper>
          <TopToolbarSubTitle>
            <Typography variant="textmdm" color={Colors.textMedium}>
              {subtileComponent}
            </Typography>
          </TopToolbarSubTitle>
        </TopToolbarTitle>
      </TopToolbarLeft>
      <TopToolbarRight>
        {insight.rightComponent && insight.rightComponent}
        {actions}
        {actions && <InsightZipped filename={insight.name} />}
        <Link href={closeRedir}>
          <CloseButton />
        </Link>
      </TopToolbarRight>
    </TopToolbarWrapper>
  )
}
